/**
 * ===================================================================================
 * 📁 경로: services/frontend/src/components/AnalysisProgress.jsx
 * 📌 프로그램명: AI 분석 실시간 진행 상태 표시 컴포넌트
 * 🧩 개요:
 * - AI 분석 파이프라인의 각 단계(PDF 분석, AI 초안 작성, 근거 탐색 등)의 진행 상태를
 * 사용자에게 시각적으로 보여주는 UI 컴포넌트입니다.
 * - 분석이 진행되는 동안, 각 단계에 맞는 유용한 정보(AI 닥터큐의 작동 원리, 윤리 원칙 등)를
 * 담은 롤링 배너를 표시하여 사용자의 대기 경험을 긍정적으로 전환시키는 역할을 합니다.
 * ===================================================================================
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './AnalysisProgress.css'; // 아래에서 추가할 CSS 파일을 임포트

/**
 * [Why]
 * AI 분석은 수십 초가 소요될 수 있는 긴 작업입니다. 사용자가 이 시간 동안 서비스가 멈췄다고
 * 느끼거나 이탈하는 것을 방지하기 위해, 현재 어떤 작업이 진행 중인지 명확하게 보여주고
 * 유용한 정보를 제공하여 긍정적인 대기 경험을 제공합니다. 이는 서비스 신뢰도와
 * 사용자 만족도에 직접적인 영향을 미칩니다.
 *
 * [How]
 * - **단계별 상태 관리**: 부모 컴포넌트로부터 받은 `progress` 배열을 기반으로 각 분석 단계의
 * 상태('pending', 'processing', 'completed')를 계산하고 시각적으로 표현합니다.
 * - **정보 롤링 배너**: 현재 진행 중인 단계에 따라 `tipsStep2`, `tipsStep3` 등 미리 정의된
 * 정보 목록(`activeTips`)을 선택합니다. `useEffect` 훅을 사용하여 일정 시간마다
 * `tipIndex` 상태를 변경, 배너의 내용이 자동으로 순환되도록 구현합니다.
 * - **자동 스크롤**: 새로운 정보 배너가 나타날 때 `useEffect`와 `useRef`를 사용하여
 * 배너 하단으로 부드럽게 스크롤하여 사용자가 새로운 내용을 놓치지 않도록 돕습니다.
 * - **콘텐츠 렌더링**: `ReactMarkdown`을 사용하여 Markdown 형식으로 작성된 팁 콘텐츠를
 * 풍부한 서식의 HTML로 렌더링합니다.
 *
 * @component
 * @param {{
* progress: Array<{step: number, status: string, message: string}>,
* error: string | null
* }} props - 이 컴포넌트가 받는 데이터 객체입니다.
* @property {Array} props.progress - 각 분석 단계의 진행 상황 정보가 담긴 배열.
* @property {string | null} props.error - 분석 중 발생한 에러 메시지.
* @returns {JSX.Element} AnalysisProgress 컴포넌트의 JSX 엘리먼트.
*/
function AnalysisProgress({ progress, error, mode = 'pdf' }) {
  // 마지막 진행 단계의 step 번호를 찾습니다.
  const lastStep = progress.length > 0 ? progress[progress.length - 1].step : 0;

  const steps = [
    { id: 1, title: mode === 'direct' ? '의료 데이터 불러오기' : 'PDF 문서 분석 및 저장' },
    { id: 2, title: 'AI 건강 리포트 초안 작성' },
    { id: 3, title: 'AI 판단 근거 설명 준비' },
    { id: 4, title: '프리마 페이시아 윤리 원칙 검토' },
    { id: 5, title: '최종 리포트 생성' },
  ];

  // 현재 진행 중인 단계를 명확히 식별하여, 해당 단계에 맞는 팁을 표시하기 위함입니다.
  // 2단계 처리 중 여부
  const step2 = progress.find(p => p.step === 2);
  const step2Processing = !!(step2 && step2.status === 'processing');
  // 3단계 처리 중 여부
  const step3 = progress.find(p => p.step === 3);
  const step3Processing = !!(step3 && step3.status === 'processing');
  // 4단계 처리 중 여부
  const step4 = progress.find(p => p.step === 4);
  const step4Processing = !!(step4 && step4.status === 'processing');
  // 5단계 처리 중 여부
  const step5 = progress.find(p => p.step === 5);
  const step5Processing = !!(step5 && step5.status === 'processing');

  // 각 분석 단계별로 사용자에게 보여줄 정보성 콘텐츠를 정의합니다.
  // useMemo를 사용하여 불필요한 배열 재생성을 방지합니다.
  const tipsStep2 = useMemo(() => ([
    `**Step 1. 흩어진 건강 데이터 모으기**\n가장 먼저, 고객님의 동의를 얻어 여러 곳에 흩어져 있는 건강 데이터를 한곳으로 안전하게 모읍니다.\n**최근 건강검진 결과, 일상 속 스마트 기기(웨어러블)의 활동 기록, 그리고 직접 입력하신 생활 습관 정보까지 모두 포함됩니다.**\n이것이 바로 고객님만을 위한 맞춤형 건강 리포트의 가장 중요한 첫걸음이자 기초 재료가 됩니다.`,
    `**Step 2. 데이터 속 숨은 의미 찾기 (AI 분석)**\n수집된 데이터를 AI가 정밀하게 분석하여 각 수치가 의미하는 바와 항목 간의 연관성을 찾아냅니다.\n**예를 들어, 높은 콜레스테롤 수치가 식습관 기록이나 운동량 부족과 어떻게 연결되는지 입체적으로 파악합니다.**\n단순한 숫자 나열을 넘어, 고객님의 현재 건강 상태에 대한 종합적인 해석을 내리는 과정입니다.`,
    `**Step 3. 의학적 지식으로 신뢰 더하기 (RAG)**\nAI의 분석 결과에 최신 의학 지식을 더해 리포트의 신뢰도와 전문성을 높입니다.\nRAG(검색 증강 생성) 기술을 통해 공신력 있는 의학 논문이나 공인된 건강 가이드라인을 실시간으로 참조합니다.\n이를 통해 **AI의 해석이 의학적 사실에 근거하고 있는지 검증하고, 가장 정확한 정보를 리포트에 담습니다.**`,
    `**Step 4. 한눈에 보이는 시각화 리포트**\n복잡한 건강 데이터를 **우선순위** 태그가 붙은 명확한 **목표 카드**로 바꾸어 보여드립니다. 각 목표 카드에는 목표 달성률을 보여주는 **진행률 바**와 **실천 기한**(예: D-90)이 표시되어, 나의 노력을 직관적으로 확인할 수 있습니다.\n이처럼 **단순히 수치를 나열하는 대신, 무엇을 해야 할지 한눈에 파악하고 실천 과정을 추적할 수 있도록 리포트를 시각화합니다.**`,
    `**Step 5. 나를 위한 맞춤 솔루션 제안**\n리포트의 마지막은 현재 건강 상태에 대한 명확한 요약과 앞으로를 위한 개인 맞춤형 실천 계획으로 마무리됩니다.\n**주의가 필요한 항목과 그 이유를 설명하고, 생활 속에서 바로 실천할 수 있는 구체적인 건강 목표와 팁을 제안합니다.**\n단순한 결과 통보가 아닌, 더 건강한 내일을 만들어갈 수 있는 실질적인 가이드를 제공하는 것입니다.`,
  ]), []);

  // Step3: AI 판단 근거 설명
  const tipsStep3 = useMemo(() => ([
  //  `**AI 닥터큐는 어떻게 똑똑하게 답변할까요?**\n**AI 닥터큐**는 답변을 드리기 전에, RAG 기술을 통해 항상 최신 의학 교과서나 연구 논문을 먼저 펼쳐보고 가장 정확한 정보를 확인합니다. 그리고 XAI(설명가능AI) 기술을 통해, 제가 왜 그렇게 판단했는지 고객님의 데이터와 의학적 근거를 들어 친절히 설명해 드립니다.\n덕분에 제 조언은 항상 최신 정보에 기반하며, 고객님께서는 **왜** 그런지를 이해하며 안심하고 저를 믿으실 수 있습니다.`,
    `**AI 닥터큐는 어떻게 똑똑하게 답변할까요?**\n**AI 닥터큐**는 답변을 드리기 전에, RAG 기술을 통해 항상 최신 의학 교과서나 연구 논문을 먼저 펼쳐보고 가장 정확한 정보를 확인합니다. 그리고 XAI(설명가능AI) 기술을 통해, 제가 왜 그렇게 판단했는지 고객님의 데이터와 의학적 근거를 들어 친절히 설명해 드립니다.`,
  ]), []);

  // Step4: 프리마 페이시아 윤리 원칙 검토
  const tipsStep4 = useMemo(() => ([
    // `Chapter 1. 시작하며\n\n하나의 정답만 있는 윤리가 있을까요? 🧐\n만약 약속을 지키는 것과 위험에 처한 사람을 돕는 것, 둘 중 하나를 선택해야 한다면 어떻게 해야 할까요?\n이런 고민을 위한 유연한 도덕 나침반을 소개합니다.`,
    // `Chapter 2. 프리마 페이시아 윤리 원칙이란?\n\n'프리마 페이시아(Prima Facie)'는 '언뜻 보기에'라는 뜻의 라틴어입니다.\n이 원칙은 절대적인 규칙이 아닌, 상황에 따라 더 중요한 의무가 생기면 변경될 수 있는 '조건부 의무'들을 제시하는 윤리 이론입니다.\n복잡한 현실 속 도덕적 딜레마를 해결하는 생각의 틀이죠.`,
    // `Chapter 3. 철학자, W. D. 로스\n\n이 이론은 20세기 영국 철학자 윌리엄 데이비드 로스가 제시했습니다.\n그는 1930년 대표 저서 **『옳음과 좋음 (The Right and the Good)』**을 통해 세상의 모든 도덕적 상황을 단 하나의 규칙으로 설명하려는 시도를 비판하며 새로운 윤리적 접근법을 세상에 알렸습니다.`,
    // `Chapter 4. 왜 새로운 이론이 필요했을까?\n\n"어떤 경우에도 거짓말은 안돼!" (칸트) vs "결과만 좋으면 괜찮아!" (공리주의)\n로스는 이처럼 너무 엄격하거나 결과에만 치중하는 이론은 현실의 복잡한 윤리 문제를 다루기 어렵다고 생각했습니다.\n그래서 더 현실적이고 다원적인 대안을 제시한 것이죠.`,
    // `Chapter 5. 7가지 핵심 의무 (1/2)\n\n'우리가 마땅히 지켜야 할 기본적인 의무들입니다.\n\n🤝 성실의 의무: 약속과 진실을 지켜야 할 의무\n❤️ 선행의 의무: 다른 사람의 선을 증진시킬 의무\n🛡️ 비해악(악행금지)의 의무: 타인에게 해를 끼치지 않을 의무`,
    // `Chapter 6. 7가지 핵심 의무 (2/2)\n\n🙏 감사의 의무: 타인이 베푼 친절에 보답할 의무\n⚖️ 정의의 의무: 능력과 공과에 맞게 분배할 의무\n🛠️ 자기 개선의 의무: 자신의 재능과 덕을 향상시킬 의무\n🔙 보상의 의무: 내가 저지른 잘못을 바로잡을 의무`,
    `**솔직한 건강 분석과 따뜻한 배려 사이의 고민**\n**의사**와 **AI 닥터큐** 모두 **검진 결과를 정확히 전달할 의무**(성실)와 **환자에게 불안감을 주지 않을 의무**(비해악)를 동시에 가집니다. 좋지 않은 결과를 직설적으로만 전하면 환자가 치료 의지를 잃을 수 있기에, 두 의무가 충돌하는 **도덕적 딜레마**가 생깁니다.`,
    `**상황을 고려한 최적의 설명**\n정해진 답은 없지만, **AI 닥터큐**는 결과의 심각성, 환자의 성향과 감정 상태를 종합적으로 고려합니다. **진실을 알리면서도 희망을 주고 치료를 독려하는** 최선의 설명 방식을 찾아내며, 이것이 딜레마 상황에서 가장 **실제적인 의무**가 됩니다.`,
    `**AI 닥터큐의 약속**\n**AI 닥터큐**는 서로 다른 윤리적 의무가 충돌할 때, **프리마 페이시아 윤리 원칙에 따라 고객님께 가장 도움이 되는 방향으로 결정**하겠습니다. 단순한 정보 분석을 넘어, 건강한 삶을 위한 지혜로운 파트너가 될 것을 약속합니다. 🧭`,
  ]), []);

  // Step5: 최종 리포트 생성
  const tipsStep5 = useMemo(() => ([
    `**나만을 위한 맞춤 건강 목표**\n**AI 닥터큐**가 고객님의 건강 데이터와 생활 패턴을 분석하여, 막연한 다짐이 아닌 구체적이고 실천 가능한 **맞춤 건강 목표**를 제시합니다.\n**이는 건강한 삶을 향한 여정의 똑똑한 첫걸음이 되어 줄 것입니다**.`,
    `**일상 속 건강 실천 가이드**\n설정된 목표를 이룰 수 있도록 식단, 운동, 수면 등 일상 속 구체적인 실천 방법을 제안합니다. **어렵지 않은 작은 습관부터 시작**하여 무리 없이 **건강한 생활 리듬**을 만들 수 있도록 곁에서 도와드립니다.\n이제 **무엇을** 할지 고민하지 말고, AI 닥터큐의 가이드를 가볍게 따라 해 보세요.`,
    `**스마트한 활동 로그 기록**\n**스마트폰, 웨어러블 기기와 연동하여 걸음, 운동, 수면 등 활동을 자동 기록**합니다. 또한, 본인이 직접 입력하여 일상의 모든 건강 활동을 간편하게 추가할 수도 있습니다.\n자동과 수동 기록을 넘나들며, 번거로움 없이 꼼꼼한 건강 일지를 완성해 보세요.`,
    `**데이터 기반 동기부여 및 피드백**\n기록된 로그를 분석해 고객님의 긍정적인 변화와 성과를 시각적 리포트로 보여드립니다. **목표 달성도에 맞춰 칭찬과 격려**를 보내고, 필요시 목표를 재조정하여 동기를 부여합니다.\n데이터로 증명되는 변화를 통해 꾸준히 건강한 습관을 이어갈 힘을 얻으세요. 🧭`,
  ]), []);

  // 현재 진행 상태에 따라 표시할 팁 배열과 배너 제목을 동적으로 결정합니다.
  const isStep2 = step2Processing;
  const isStep3 = !isStep2 && step3Processing;
  const isStep4 = !isStep2 && !isStep3 && step4Processing;
  const isStep5 = !isStep2 && !isStep3 && !isStep4 && step5Processing;
  const activeTips = isStep2
    ? tipsStep2
    : isStep3
    ? tipsStep3
    : isStep4
    ? tipsStep4
    : isStep5
    ? tipsStep5
    : [];
  const isBannerActive = activeTips.length > 0;
  const bannerTitle = isStep2
    ? 'AI 닥터큐는 어떻게 건강 리포트를 만드는가요?'
    : isStep3
    ? 'AI 판단 근거는 어떻게 제시하는가요?'
    : isStep4
    ? 'AI 닥터큐의 프리마 페이시아 윤리 원칙 기반 검토'
    : isStep5
    ? 'AI 닥터큐의 건강 실천 제안 및 활동 로그 기록을 통한 건강 생활 실천'
    : '';

  // 현재 스텝 키 (인터벌 변경 감지용)
  const currentStepKey = isStep2 ? 's2' : isStep5 ? 's5' : isStep3 ? 's3' : isStep4 ? 's4' : '';

  const [tipIndex, setTipIndex] = useState(0);

  // 롤링 배너 하단으로 스크롤하기 위한 ref
  const bannerEndRef = useRef(null);

  // 롤링 배너의 내용을 일정 시간 간격으로 변경하는 로직입니다.
  // step2(step2), step3(step3), step4(step4), step5(step5) 진행 중일 때만 인터벌 시작
  useEffect(() => {
    if (!isBannerActive) return;
    setTipIndex(0);  // 스텝이 변경되면 항상 첫 번째 팁부터 보여줍니다.
    const intervalMs = isStep2 ? 20000 : isStep3 ? 15000 : isStep4 ? 12000 : isStep5 ? 15000 : 12000;
    // 모든 tips를 한 번씩 보여준 뒤 마지막 문구에서 멈춤
    let idx = 0;
    let timerId = 0;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (idx >= activeTips.length - 1) {
        // 마지막 팁 이후에는 멈춥니다.
        return;
      }
      idx += 1;
      setTipIndex(idx);
      timerId = window.setTimeout(tick, intervalMs);
    };
    timerId = window.setTimeout(tick, intervalMs);
    // 컴포넌트가 언마운트되거나 스텝이 변경될 때 타이머를 정리합니다.
    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBannerActive, activeTips.length, currentStepKey]);  // 스텝이 바뀔 때마다 이 효과를 재실행합니다.

  // 롤링 배너가 활성화되거나 스텝이 변경될 때, 배너 하단으로 화면을 부드럽게 스크롤합니다.
  useEffect(() => {
    if (!isBannerActive) return;
    // 다음 프레임에 수행하여 DOM이 그려진 후 스크롤
    const t = window.requestAnimationFrame(() => {
      if (bannerEndRef.current) {
        try {
          bannerEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        } catch (_) {
          // scrollIntoView 미지원 브라우저 대비 폴백
          const rect = bannerEndRef.current.getBoundingClientRect();
          window.scrollTo({ top: rect.bottom + window.scrollY - window.innerHeight, behavior: 'smooth' });
        }
      }
    });
    return () => window.cancelAnimationFrame(t);
  }, [isBannerActive, currentStepKey]);

  return (
    <div className="progress-container">
      <h3 className="progress-title">AI 닥터큐가 분석 중입니다...</h3>
      <ul className="progress-list">
        {steps.map((step) => {
          const currentProgress = progress.find(p => p.step === step.id);
          let status = 'pending'; // 대기
          if (currentProgress) {
            status = currentProgress.status; // processing 또는 completed
          } else if (step.id < lastStep) {
            status = 'completed'; // 이전 단계는 완료 처리
          }

          return (
            <li key={step.id} className={`progress-item status-${status}`}>
              <div className="progress-icon">
                {status === 'completed' ? '✔️' : '⚙️'}
              </div>
              <div className="progress-text">
                <span className="step-title">{step.title}</span>
                {status === 'processing' && currentProgress && currentProgress.message && 
                  <span className="step-message">{currentProgress.message}</span>
                }
                {status === 'completed' && currentProgress && currentProgress.message && 
                  <span className="step-message">
                    {currentProgress.message.replace('하고 있습니다', '완료하였습니다').replace('검토 중입니다', '검토 완료하였습니다').replace('생성 중입니다', '생성 완료하였습니다')}
                  </span>
                }
              </div>
            </li>
          );
        })}
      </ul>
      {/* step2 진행 중엔 Part1, step5 진행 중엔 Part2 배너 노출 */}
      {isBannerActive && (
        <div className="analysis-tips-banner" role="status" aria-live="polite" aria-atomic="true">
          <div className="tips-title">{bannerTitle}</div>
          <div className="tips-content tips-fade" key={tipIndex}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {activeTips[tipIndex]}
            </ReactMarkdown>
          </div>
          {/* 스크롤 기준점: 배너 하단 */}
          <div ref={bannerEndRef} style={{ height: 1 }} />
        </div>
      )}
      {error && <div className="error-message">Error: {error}</div>}
    </div>
  );
}

export default AnalysisProgress;
